import { Instance, applySnapshot, flow, getParent, getSnapshot, types } from 'mobx-state-tree';

import { ImpersonateResponse, getImpersonateToken } from '@common/api/auth';
import { attachRole, detachRole, getUser, resetUserPassword } from '@common/api/users';
import { enqueueSnackbar } from '@common/components/SnackBar';
import Location from '@common/models/Locations/Location';
import Service from '@common/models/Services/Service';
import { storeReference } from '@common/models/helpers';

import Media from '../Media';
import { IUsersModel } from './Users';

const User = types
	.model('UserModel', {
		id: types.identifierNumber,
		first_name: types.optional(types.maybeNull(types.string), ''),
		last_name: types.optional(types.maybeNull(types.string), ''),
		email: types.optional(types.string, ''),
		user_id: types.maybeNull(types.number),
		locations_ids: types.optional(types.array(storeReference(Location)), []),
		services_ids: types.optional(types.array(storeReference(Service)), []),
		businesses_ids: types.optional(types.array(types.number), []),
		password_email_sent: types.optional(types.number, 0),
		avatar_url: types.maybeNull(types.string),
		avatar: types.maybeNull(Media),
		created_at: types.maybe(types.maybeNull(types.string)),
		last_login_at: types.maybe(types.maybeNull(types.string)),
	})
	.volatile(() => ({
		loading: false,
		reloading: false,
		isRoleAttached: false,
	}))
	.views((self) => ({
		get fullName() {
			return [self.first_name, self.last_name].join(' ');
		},
		get fullNameByLastName() {
			return [self.last_name, self.first_name].join(' ');
		},
		get nameInitials() {
			return `${self.first_name[0]}${self.last_name[0]}`;
		},
	}))
	.actions(() => ({
		_fetch: getUser,
		_save: (_: number, _data: any) => new Promise(() => {}),
		_remove: (_: number) => new Promise(() => {}),
	}))
	.actions((self) => ({
		fetch: flow(function* () {
			self.loading = true;
			try {
				const result = yield self._fetch(self.id);
				applySnapshot(self, result);
			} finally {
				self.loading = false;
			}
		}),
		save: flow(function* ({ ...data }: any) {
			self.reloading = true;
			const formateData = {
				...data,
				avatar_url: data.avatar?.url ? data.avatar.url : null,
			};
			try {
				applySnapshot(self, formateData);
				const result = yield self._save(self.id, getSnapshot(self));
				applySnapshot(self, result);
			} finally {
				self.reloading = false;
			}
		}),
		remove: flow(function* () {
			self.reloading = true;
			try {
				yield self._remove(self.id);
				const parent = getParent<IUsersModel>(self, 2);
				parent.removeItem(self as IUserModel);
			} catch (e) {
				self.reloading = false;
			}
		}),
		impersonateToken: flow(function* (): Generator<
			Promise<ImpersonateResponse>,
			string,
			ImpersonateResponse
		> {
			try {
				const response = yield getImpersonateToken(self.user_id);
				return response.token;
			} catch (error) {
				console.error('Error getting impersonate token:', error);
			}
		}),
		resetUserPassword: flow(function* (): Generator<Promise<any>, string, any> {
			self.reloading = true;
			try {
				// self.id for admins
				const response = yield resetUserPassword(self.user_id || self.id);
				self.reloading = false;
				return response;
			} finally {
				self.reloading = false;
			}
		}),
		attachRole: flow(function* (roleId: number) {
			try {
				self.reloading = true;
				yield attachRole(self.user_id, roleId, {});
				enqueueSnackbar('Success', {
					variant: 'success',
				});
				self.isRoleAttached = true;
			} finally {
				self.reloading = false;
			}
		}),
		detachRole: flow(function* (roleId: number) {
			try {
				self.reloading = true;
				yield detachRole(self.user_id, roleId);
				enqueueSnackbar('Success', {
					variant: 'success',
				});
				self.isRoleAttached = true;
			} finally {
				self.reloading = false;
			}
		}),
	}));

export default User;

export interface IUserModel extends Instance<typeof User> {}
